// src/pages/Auth/ForgotPassword.jsx
import React, { useState } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box,
  Alert, CircularProgress, Link,
} from '@mui/material';
import { LockReset, ArrowBack, Email } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { authAPI } from '../../services/api';
import toast from 'react-hot-toast';

const ForgotPassword = () => {
  const [email,     setEmail]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [sent,      setSent]      = useState(false);
  const [error,     setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email address'); return; }
    setLoading(true);
    setError('');
    try {
      await authAPI.forgotPassword(email.trim().toLowerCase());
      setSent(true);
      toast.success('Reset link sent!');
    } catch (err) {
      setError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 10, mb: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LockReset sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>Forgot Password</Typography>
            <Typography variant="body1" color="text.secondary">
              Enter your registered email address and we'll send you a password reset link.
            </Typography>
          </Box>

          {sent ? (
            <Box sx={{ textAlign: 'center' }}>
              <Email sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Alert severity="success" sx={{ mb: 3 }}>
                <strong>Reset link sent!</strong><br />
                Check your email inbox (and spam folder) for the reset link. It expires in 2 hours.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Didn't receive it?{' '}
                <Link component="button" onClick={() => { setSent(false); }} color="primary">
                  Try again
                </Link>
              </Typography>
              <Button component={RouterLink} to="/login" variant="contained" startIcon={<ArrowBack />}>
                Back to Login
              </Button>
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                disabled={loading}
                autoComplete="email"
                autoFocus
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !email}
                startIcon={loading ? <CircularProgress size={20} /> : <LockReset />}
                sx={{ mb: 2, py: 1.5 }}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to="/login" color="primary" underline="hover">
                  <ArrowBack sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                  Back to Login
                </Link>
              </Box>
            </form>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
